import { apiRequest } from "@services/apiClient";
import { fetchOutreachTreatments } from "@services/outreach";

/**
 * Reactivation campaigns: the call list is fetched by services/scheduling.js;
 * everything else about campaigns, treatments and patient status lives here.
 *
 * Every function unwraps the `{ data, message }` envelope and normalises the
 * shape, so components never see the envelope. apiRequest throws
 * `new Error(message)` on a non-2xx, so callers only get `err.message`.
 */

const BASE = "/scheduling/reactivation";

function withClinic(endpoint, clinicId) {
  return clinicId ? `${endpoint}?clinic_id=${encodeURIComponent(clinicId)}` : endpoint;
}

/** Campaigns for a clinic, newest first. Always an array. */
export async function fetchCampaigns(clinicId) {
  const response = await apiRequest({
    method: "GET",
    endpoint: withClinic(`${BASE}/campaigns`, clinicId),
  });
  const campaigns = response?.data?.campaigns;
  return Array.isArray(campaigns) ? campaigns : [];
}

/** One campaign with its contacts and whether it can dial right now. */
export async function fetchCampaign(campaignId, clinicId) {
  const response = await apiRequest({
    method: "GET",
    endpoint: withClinic(`${BASE}/campaigns/${campaignId}`, clinicId),
  });
  const data = response?.data ?? {};
  return {
    campaign: data.campaign ?? null,
    contacts: Array.isArray(data.contacts) ? data.contacts : [],
    canDialNow: Boolean(data.can_dial_now),
    nextDialAt: data.next_dial_at ?? null,
  };
}

/**
 * Start a campaign. Pass `patients` (the rows the user ticked) or
 * `patientCount` (how many the backend should draw at random), never both.
 * `mode` ("book" | "transfer"), `pitch` and `dailyNewContactCap` are sent
 * only when given; the server defaults them (book, none, unlimited).
 * `requested` and `enrolled` say how many were asked for and how many the
 * campaign really holds; they are null if the server did not say.
 */
export async function createCampaign({
  clinicId, treatmentId, patients, patientCount, name, smsConfirmationEnabled,
  providerExternalId, mode, pitch, dailyNewContactCap,
}) {
  const body = { treatment_id: treatmentId };
  if (patientCount !== undefined) body.patient_count = patientCount;
  else body.patients = patients || [];
  if (clinicId) body.clinic_id = clinicId;
  if (name) body.name = name;
  if (providerExternalId) body.provider_external_id = providerExternalId;
  if (mode) body.mode = mode;
  if (pitch) body.pitch = pitch;
  if (dailyNewContactCap !== undefined && dailyNewContactCap !== null) {
    body.daily_new_contact_cap = dailyNewContactCap;
  }
  if (smsConfirmationEnabled !== undefined) {
    body.sms_confirmation_enabled = smsConfirmationEnabled;
  }
  const response = await apiRequest({ method: "POST", endpoint: `${BASE}/campaigns`, body });
  const data = response?.data ?? {};
  return {
    campaign: data.campaign ?? null,
    skipped: Array.isArray(data.skipped) ? data.skipped : [],
    requested: Number.isInteger(data.requested) ? data.requested : null,
    enrolled: Number.isInteger(data.enrolled) ? data.enrolled : null,
  };
}

function control(action) {
  return async (campaignId, clinicId) => {
    const response = await apiRequest({
      method: "POST",
      endpoint: withClinic(`${BASE}/campaigns/${campaignId}/${action}`, clinicId),
    });
    return response?.data?.campaign ?? null;
  };
}

export const pauseCampaign = control("pause");
export const resumeCampaign = control("resume");
export const cancelCampaign = control("cancel");

/** Flag or unflag a patient so campaigns and the call list skip them. */
export async function setDoNotCall(externalPatientId, { doNotCall, reason, clinicId }) {
  const body = { do_not_call: Boolean(doNotCall) };
  if (reason) body.reason = reason;
  if (clinicId) body.clinic_id = clinicId;
  const response = await apiRequest({
    method: "PUT",
    endpoint: `${BASE}/patients/${encodeURIComponent(externalPatientId)}/do-not-call`,
    body,
  });
  return response?.data ?? null;
}

/**
 * The clinic's bookable procedures. Only active ones can start a campaign.
 *
 * This is the outreach treatment whitelist -- same endpoint, same clinic
 * scoping, same normalisation -- narrowed to the rows a campaign may offer,
 * so it wraps that call rather than keeping a second copy of the contract.
 */
export async function fetchTreatments(clinicId) {
  const rows = await fetchOutreachTreatments({ clinicId });
  return rows.filter((row) => row.is_active);
}

/**
 * The clinic's synced providers, for the "Book with" choice when starting a
 * campaign. Shape:
 * [{ external_provider_id, name, is_default, manual_schedule,
 *    manual_schedule_updated_at, learned_schedule,
 *    schedule_source ("manual" | "learned" | "none") }]. Always an array.
 */
export async function fetchProviders(clinicId) {
  const response = await apiRequest({
    method: "GET",
    endpoint: withClinic(`${BASE}/providers`, clinicId),
  });
  const rows = response?.data?.providers;
  return Array.isArray(rows) ? rows : [];
}

const DEFAULT_SEGMENT_CHARS = 160;
// Matches the column default on the server.
const DEFAULT_REPORT_TIME = "16:30";

const slotPair = (v) => (Array.isArray(v) && v.length === 2 ? [String(v[0]), String(v[1])] : ["", ""]);

/** Camel-cased settings, so components never handle the API's snake_case. */
function toSettings(data) {
  const source = data ?? {};
  return {
    clinicId: source.clinic_id ?? null,
    cooldownDays: source.cooldown_days ?? 0,
    attemptGapsDays: Array.isArray(source.attempt_gaps_days) ? source.attempt_gaps_days : [],
    callingSlots: {
      morning: slotPair(source.calling_slots?.morning),
      afternoon: slotPair(source.calling_slots?.afternoon),
      evening: slotPair(source.calling_slots?.evening),
    },
    callingDays: Array.isArray(source.calling_days) ? source.calling_days : [],
    smsEnabled: Boolean(source.sms_confirmation_enabled),
    smsTemplate: source.sms_confirmation_template ?? "",
    placeholders: Array.isArray(source.sms_placeholders) ? source.sms_placeholders : [],
    segmentChars: source.sms_segment_chars ?? DEFAULT_SEGMENT_CHARS,
    dailyReportEnabled: Boolean(source.daily_report_enabled),
    dailyReportRecipients: Array.isArray(source.daily_report_recipients)
      ? source.daily_report_recipients : [],
    dailyReportTime: source.daily_report_time ?? DEFAULT_REPORT_TIME,
    // null is "no pause", and 0 is a pause of no length; both are kept as is.
    transferHoldMinutes: source.transfer_hold_minutes ?? null,
  };
}

/** The clinic's reactivation settings, created with defaults on first read. */
export async function fetchSettings(clinicId) {
  const response = await apiRequest({
    method: "GET",
    endpoint: withClinic(`${BASE}/settings`, clinicId),
  });
  return toSettings(response?.data);
}

/**
 * Save the settings that changed. Only the keys present are sent, so an
 * untouched field is never overwritten, and an empty body is a 400. Returns
 * what the server stored, with the protocol fields normalised.
 */
export async function saveSettings({
  clinicId, cooldownDays, attemptGapsDays, callingSlots, callingDays, smsEnabled, smsTemplate,
  dailyReportEnabled, dailyReportRecipients, dailyReportTime, transferHoldMinutes,
}) {
  const body = {};
  if (clinicId) body.clinic_id = clinicId;
  if (cooldownDays !== undefined) body.cooldown_days = cooldownDays;
  if (attemptGapsDays !== undefined) body.attempt_gaps_days = attemptGapsDays;
  if (callingSlots !== undefined) body.calling_slots = callingSlots;
  if (callingDays !== undefined) body.calling_days = callingDays;
  if (smsEnabled !== undefined) body.sms_confirmation_enabled = smsEnabled;
  if (smsTemplate !== undefined) body.sms_confirmation_template = smsTemplate;
  if (dailyReportEnabled !== undefined) body.daily_report_enabled = dailyReportEnabled;
  if (dailyReportRecipients !== undefined) body.daily_report_recipients = dailyReportRecipients;
  if (dailyReportTime !== undefined) body.daily_report_time = dailyReportTime;
  // null is a value here: it is how the pause is turned off.
  if (transferHoldMinutes !== undefined) body.transfer_hold_minutes = transferHoldMinutes;
  const response = await apiRequest({
    method: "PUT", endpoint: `${BASE}/settings`, body,
  });
  return toSettings(response?.data);
}

/**
 * Every lead still in the loop for the clinic, across campaigns: who is
 * being called now, who is waiting for a retry or a callback, and who is
 * parked in a paused campaign. Always an array.
 */
export async function fetchQueue(clinicId) {
  const response = await apiRequest({
    method: "GET",
    endpoint: withClinic(`${BASE}/queue`, clinicId),
  });
  const leads = response?.data?.leads;
  return Array.isArray(leads) ? leads : [];
}

/** Patients who must never be called until someone clears the flag. */
export async function fetchDoNotCall(clinicId) {
  const response = await apiRequest({
    method: "GET",
    endpoint: withClinic(`${BASE}/do-not-call`, clinicId),
  });
  const patients = response?.data?.patients;
  return Array.isArray(patients) ? patients : [];
}

/** Query string from a params object, skipping blanks. */
function withParams(endpoint, params) {
  const qs = Object.entries(params)
    .filter(([, v]) => v !== undefined && v !== null && v !== "")
    .map(([k, v]) => `${encodeURIComponent(k)}=${encodeURIComponent(v)}`)
    .join("&");
  return qs ? `${endpoint}?${qs}` : endpoint;
}

function analyticsParams({ clinicId, campaignId, from, to } = {}) {
  return { clinic_id: clinicId, campaign_id: campaignId, from, to };
}

/** Campaign numbers for the Overview tab. Always an object. */
export async function fetchAnalytics(params = {}) {
  const response = await apiRequest({
    method: "GET",
    endpoint: withParams(`${BASE}/analytics`, analyticsParams(params)),
  });
  const data = response?.data;
  return data && typeof data === "object" ? data : {};
}

const UNAVAILABLE = { status: "unavailable", reason: "empty" };

/** The written summary, or why there is none. */
export async function fetchInsights(params = {}) {
  const response = await apiRequest({
    method: "GET",
    endpoint: withParams(`${BASE}/analytics/insights`, analyticsParams(params)),
  });
  const data = response?.data;
  return data && typeof data === "object" ? data : UNAVAILABLE;
}

/** Ask for a fresh summary. The server answers 429 when asked too often. */
export async function regenerateInsights(params = {}) {
  const response = await apiRequest({
    method: "POST",
    endpoint: withParams(`${BASE}/analytics/insights/regenerate`, analyticsParams(params)),
  });
  const data = response?.data;
  return data && typeof data === "object" ? data : UNAVAILABLE;
}
